import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OnelivePageLayout from './OnelivePageLayout';
import OneliveSubPageLayout from './OneliveSubPageLayout';
import { oneliveAssets } from './assets';
import {
  cacheItemShopOrderItem,
  fetchItemShopItems,
  formatAmount,
  isVipServiceShopItem,
  navigate,
  resolveItemPurchaseBlock,
  resolveItemShopImageUrl,
  showOneliveAlert,
  type ItemShopItem,
} from './api';
import { handleLoginRequired } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';
import { useOneliveSession } from './useOneliveSession';
import { isLoginGatePath } from './authRedirect';

function isGridItem(item: ItemShopItem): boolean {
  return !isVipServiceShopItem(item);
}

function isQuickBannerItem(item: ItemShopItem): boolean {
  return isVipServiceShopItem(item);
}

export default function OneliveItemShopPage() {
  const [items, setItems] = useState<ItemShopItem[]>([]);
  const [chargeAmount, setChargeAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const { user, isAuthed, authReady } = useOneliveSession();
  const liveUiMessages = useLiveUiMessages();
  const loginGuardedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!authReady || isAuthed || loginGuardedRef.current) return;
    if (!isLoginGatePath(window.location.pathname)) return;
    loginGuardedRef.current = true;
    handleLoginRequired(liveUiMessages, '/shop');
  }, [authReady, isAuthed, liveUiMessages]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const itemList = await fetchItemShopItems();
      setItems(itemList);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authReady) return;
    if (!isAuthed) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, authReady, isAuthed]);

  const packageItems = useMemo(
    () => items.filter((item) => isGridItem(item)),
    [items],
  );
  const vipBanner = useMemo(
    () => items.find((item) => isQuickBannerItem(item)),
    [items],
  );
  const quickServiceItems = useMemo(
    () => items.filter((item) => isQuickBannerItem(item)),
    [items],
  );

  const goOrder = (target: ItemShopItem) => {
    if (!isAuthed) {
      handleLoginRequired(liveUiMessages, `/shop/order/${target.id}`);
      return;
    }

    const blockReason = resolveItemPurchaseBlock(target, user?.member_level ?? 2);
    if (blockReason) {
      showOneliveAlert(blockReason);
      return;
    }

    cacheItemShopOrderItem(target);
    navigate(`/shop/order/${target.id}`);
  };

  const onPurchaseVip = () => {
    if (!isAuthed) {
      handleLoginRequired(liveUiMessages, '/shop');
      return;
    }
    if (!vipBanner) {
      showOneliveAlert('상품 정보를 불러올 수 없습니다.');
      return;
    }
    goOrder(vipBanner);
  };

  const onCharge = (e: FormEvent) => {
    e.preventDefault();
    if (!isAuthed) {
      handleLoginRequired(liveUiMessages, '/shop');
      return;
    }
    const amount = Number(chargeAmount);
    if (!Number.isFinite(amount) || amount < 1000) {
      showOneliveAlert('충전 금액은 최소 1,000원 이상이어야 합니다.');
      return;
    }
    navigate(`/shop/charge?amount=${amount}`);
  };

  return (
    <OnelivePageLayout pageClassName="onelive-item-shop-page">
      <OneliveSubPageLayout title="아이템샵">
        {!isAuthed ? (
          <p className="onelive-loading">로딩 중...</p>
        ) : loading ? (
          <p className="onelive-loading">로딩 중...</p>
        ) : (
          <>
            <div className="item-box">
              <div className="proceeding-chat pad-0">
                <div className="pro-chat-tit">
                  <h3>
                    <span><img src={oneliveAssets.itemSection} alt="" /></span>
                    패키지구매
                  </h3>
                </div>
              </div>

              <div className="item-group">
                {packageItems.map((item) => (
                  <div key={item.id} className="item">
                    <div className="item-img">
                      <img src={resolveItemShopImageUrl(item.img)} alt="" />
                    </div>
                    <div className="item-tit">{item.name}</div>
                    <div className="item-text">
                      +코인<span>{formatAmount(item.coin)}</span>개
                    </div>
                    <div className="item-cash">{formatAmount(item.price)}원</div>
                    <div className="item-btn">
                      <button
                        type="button"
                        className="btn button-orange"
                        onClick={() => goOrder(item)}
                      >
                        <img src={oneliveAssets.shopBuy} alt="" />
                        구매하기
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {vipBanner ? (
                <div className="quick-baner mar-t-10">
                  <a href="#n" onClick={(ev) => { ev.preventDefault(); onPurchaseVip(); }}>
                    <img src={resolveItemShopImageUrl(vipBanner.img)} alt={vipBanner.name} />
                  </a>
                  <div className="quick-item-text">
                    {vipBanner.name}
                    <span>{formatAmount(vipBanner.price)}원</span>
                  </div>
                  <div className="quick-order">
                    <button type="button" className="btn" onClick={onPurchaseVip}>
                      <img src={oneliveAssets.shopBuy} alt="" />
                      구매하기
                    </button>
                  </div>
                </div>
              ) : quickServiceItems.length > 0 ? (
                quickServiceItems.map((item) => (
                  <div key={item.id} className="quick-baner mar-t-10">
                    <a href="#n" onClick={(ev) => { ev.preventDefault(); goOrder(item); }}>
                      <img src={resolveItemShopImageUrl(item.img)} alt={item.name} />
                    </a>
                    <div className="quick-item-text">
                      {item.name}
                      <span>{formatAmount(item.price)}원</span>
                    </div>
                    <div className="quick-order">
                      <button type="button" className="btn" onClick={() => goOrder(item)}>
                        <img src={oneliveAssets.shopBuy} alt="" />
                        구매하기
                      </button>
                    </div>
                  </div>
                ))
              ) : null}

              <div className="proceeding-chat mar-t-35 pad-0">
                <div className="pro-chat-tit">
                  <h3>
                    <span><img src={oneliveAssets.coinCharge} alt="" /></span>
                    코인 충전
                  </h3>
                </div>
              </div>

              <form className="charge" onSubmit={onCharge}>
                <input
                  type="text"
                  inputMode="numeric"
                  className="text-center form-control"
                  maxLength={10}
                  autoComplete="off"
                  placeholder="충전하실 금액을 입력해 주세요."
                  value={chargeAmount}
                  onChange={(ev) => setChargeAmount(ev.target.value.replace(/\D/g, ''))}
                />
                <button type="submit" className="btn btn-info">
                  확인
                </button>
              </form>
            </div>

            <div className="gray-box">
              <h3 className="i-title">유의사항</h3>
              <ul className="td-ul">
                <li>본 상품은 구매 후 7일 이내에는 사용하지 않은 부분에 대해 취소 및 환불이 가능합니다.</li>
                <li>단, 상품 내용이 표시, 광고의 내용과 다르거나 계약내용과 다르게 이행된 경우에는 결제일로부터 3개월 이내, 그 사실을 안 날 또는 알 수 있었던 날부터 30일 이내에 취소/환불이 가능합니다.</li>
                <li>취소/환불신청은 1:1문의 요청에 남겨주시면 처리를 도와드리겠습니다.</li>
                <li>충전한 코인의 유효기간은 5년입니다.</li>
              </ul>
            </div>
          </>
        )}
      </OneliveSubPageLayout>
    </OnelivePageLayout>
  );
}
